import React from 'react';
import { Link } from 'react-router-dom';
import { Star, ShieldCheck, Heart, Zap, Clock } from 'lucide-react';
import { Listing } from '../../types';
import { useCurrency } from '../../context/CurrencyContext';

interface ListingCardProps {
  listing: Listing;
  onWishlistToggle?: (id: string) => void;
  isWishlisted?: boolean;
}

export const ListingCard: React.FC<ListingCardProps> = ({ listing, onWishlistToggle, isWishlisted }) => {
  const { displayPrice } = useCurrency();
  const priceInfo = displayPrice(listing.price_cents);

  // Fallback image
  const thumbnail = (listing.images && listing.images.length > 0)
    ? listing.images[0]
    : 'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=600';

  return (
    <div className="group relative rounded-2xl bg-white dark:bg-brand-darkCard border border-brand-lightBorder dark:border-brand-darkBorder hover:border-brand-blue/50 dark:hover:border-brand-accent/50 shadow-sm hover:shadow-glow-blue transition-all duration-300 flex flex-col overflow-hidden">
      
      {/* Top Image Preview & Badges */}
      <div className="relative aspect-[16/9] w-full overflow-hidden bg-slate-900">
        <img
          src={thumbnail}
          alt={listing.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

        {/* Category Pill */}
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 flex-wrap">
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-black/60 backdrop-blur-md text-white border border-white/10">
            {listing.category_slug}
          </span>
          {listing.delivery_type === 'automatic' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/90 text-black">
              <Zap className="w-3 h-3 fill-current" /> Instant
            </span>
          )}
        </div>

        {/* Wishlist Toggle Button */}
        {onWishlistToggle && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onWishlistToggle(listing.id);
            }}
            className={`absolute top-2.5 right-2.5 p-1.5 rounded-full backdrop-blur-md transition-colors ${
              isWishlisted
                ? 'bg-rose-500 text-white'
                : 'bg-black/50 text-white/80 hover:text-white hover:bg-black/80'
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${isWishlisted ? 'fill-current' : ''}`} />
          </button>
        )}

        {/* Game Icon & Name Overlay */}
        <div className="absolute bottom-2.5 left-2.5 flex items-center gap-2">
          {listing.game_icon && (
            <img src={listing.game_icon} alt="" className="w-5 h-5 rounded-md object-cover border border-white/20" />
          )}
          <span className="text-xs font-semibold text-white/90 drop-shadow">
            {listing.game_name}
          </span>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4 flex-1 flex flex-col justify-between">
        <div>
          {/* Title */}
          <Link to={`/product/${listing.id}`}>
            <h3 className="font-bold text-sm text-brand-lightText dark:text-brand-darkText line-clamp-2 hover:text-brand-blue dark:hover:text-brand-accent transition-colors">
              {listing.title}
            </h3>
          </Link>

          {/* Dynamic Attributes Preview */}
          {listing.attributes && Object.keys(listing.attributes).length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {Object.entries(listing.attributes).slice(0, 3).map(([key, val]) => (
                <span key={key} className="px-2 py-0.5 rounded-md text-[10px] bg-brand-lightSec dark:bg-brand-darkElevated text-brand-lightSecText dark:text-brand-darkSecText border border-brand-lightBorder dark:border-brand-darkBorder">
                  {key}: <strong className="text-brand-lightText dark:text-brand-darkText">{String(val)}</strong>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Seller Info & Price Footer */}
        <div className="mt-4 pt-3 border-t border-brand-lightBorder dark:border-brand-darkBorder flex items-center justify-between">
          {/* Seller Store & Rating */}
          <div className="flex flex-col">
            <span className="text-[11px] font-semibold text-brand-lightText dark:text-brand-darkText truncate max-w-[130px]">
              {listing.store_name || 'Verified Seller'}
            </span>
            <div className="flex items-center gap-1 text-[10px] text-amber-500 font-bold">
              <Star className="w-3 h-3 fill-current" />
              <span>{listing.seller_rating || 5.0}</span>
              <span className="text-brand-lightSecText dark:text-brand-darkSecText font-normal">
                ({listing.seller_orders || 12}+ sold)
              </span>
            </div>
          </div>

          {/* Price */}
          <div className="text-right">
            <div className="flex items-baseline justify-end gap-1.5">
              {listing.discount_percent > 0 && (
                <span className="text-[10px] text-brand-lightSecText line-through">
                  ${(listing.price_cents / 100).toFixed(2)}
                </span>
              )}
              <span className="font-extrabold text-base text-brand-blue dark:text-brand-accent">
                {priceInfo.formatted}
              </span>
            </div>
            <span className="text-[9px] text-brand-lightSecText dark:text-brand-darkSecText block">
              {priceInfo.chargedUsd}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
